import { call, put, select, spawn } from 'redux-saga/effects';
import { formControlActions } from 'store/formControlSlice/formControlSlice';
import { selectFormsSlice } from 'store/formControlSlice/formControlSelectors';
import { selectFeatureFlagData } from 'store/featureFlag/featureFlagSelector';
import { fetchInventory } from 'services/inventoryServices';
import { fetchEquipment } from 'services/equipmentServices';
import { notificationHandler as notificationsHandler } from 'utils/utils';

// Transactions of one location opened together share a single request. axiosAiq has no timeout, so
// a request older than this is presumed stuck: the next open sends its own instead of waiting on it.
export const SHARE_IN_FLIGHT_REQUEST_MS = 30000;

// Per kind + location: the latest request sent, and the sequence number of the one behind the stored
// data. Age uses the monotonic performance.now() and order a counter, so a system clock change can
// neither keep a stuck request shared nor make fresh responses look older.
const latestRequests = new Map();
const storedRequestSequence = new Map();
let requestSequence = 0;

const hasOpenFormForLocation = (forms, locationId) =>
    Object.values(forms ?? {}).some(({ transactionData }) => String(transactionData?.gate?.location?.id) === String(locationId));

export function* loadLocationLookupData({ kind, locationId, fetchData, buildStoreAction, errorTitle }) {
    const requestKey = `${kind}_${locationId}`;
    const inFlight = latestRequests.get(requestKey);
    if (inFlight && performance.now() - inFlight.startedAt < SHARE_IN_FLIGHT_REQUEST_MS) return;

    requestSequence += 1;
    const request = { startedAt: performance.now(), sequence: requestSequence };
    latestRequests.set(requestKey, request);
    try {
        const data = yield call(fetchData, locationId);
        // A replaced request still fills the dropdowns until the newer one answers, but never
        // overwrites what that newer one already stored.
        if ((storedRequestSequence.get(requestKey) ?? 0) > request.sequence) return;
        // closeTransactionPanel clears a location's data once its last form closes; a response that
        // lands after that must not put it back.
        const { forms } = yield select(selectFormsSlice);
        if (data && hasOpenFormForLocation(forms, locationId)) {
            storedRequestSequence.set(requestKey, request.sequence);
            yield put(buildStoreAction(data));
        }
    } catch (err) {
        if (latestRequests.get(requestKey) === request) yield call(notificationsHandler, { err, title: errorTitle });
    } finally {
        if (latestRequests.get(requestKey) === request) latestRequests.delete(requestKey);
    }
}

// Inventory and equipment only feed the dropdowns and the section indicators, which render from the
// store whenever the data arrives, so they are spawned: they must neither hold up nor fail the form.
export function* startLocationLookupDataLoad({ locationId, inventoryMode }) {
    const { inventoryEnabled = false, clientEquipmentEnabled = false } = yield select(selectFeatureFlagData);

    if (inventoryEnabled && inventoryMode === 'ON') {
        yield spawn(loadLocationLookupData, {
            kind: 'inventory',
            locationId,
            fetchData: fetchInventory,
            buildStoreAction: (inventoryData) => formControlActions.setInventoryData({ inventoryData, locationId }),
            errorTitle: 'Error fetching inventory data',
        });
    }

    if (clientEquipmentEnabled) {
        yield spawn(loadLocationLookupData, {
            kind: 'equipment',
            locationId,
            fetchData: fetchEquipment,
            buildStoreAction: (equipmentData) => formControlActions.setEquipmentData({ equipmentData, locationId }),
            errorTitle: 'Error fetching equipment data',
        });
    }
}
